import { Injectable } from '@angular/core'
import { ToastrService } from 'ngx-toastr'
import { PracticeBatchInfo, PricingChangeRequest, PricingManagerClient } from './ppv.api'
import { VetcorPricingRowService } from 'src/app/vetcor-pricing/vetcor-pricing-table/vetcor-pricing-row.service'
import { Observable } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class PriceActionService {
   constructor(private toastr: ToastrService, private pricingClient: PricingManagerClient, private pricingService: VetcorPricingRowService) {}

   /*
   isValidPrice(value: any): boolean {
      const regex = /^\d+(\.\d+)?$/
      return regex.test(value?.toString())
   }

   saveOpsPrice(product: any, passedScheduleDate: boolean, onSuccess: (data?: any) => void, onError: () => void): void {
      const isZoho = !!product.zoho

      if (!isZoho && (product.opsPrice === 'NaN' || !this.isValidPrice(product.opsPrice))) {
         this.toastr.error('Only numeric values allowed')
         return
      }

      if (!isZoho && product.opsPrice <= 0) {
         this.toastr.error('Value must be greater than 0')
         return
      }

      if (!product.finalComment?.trim()) {
         this.toastr.error('Rationale is required to continue')
         return
      }

      if (product.finalComment.trim().length <= 5) {
         this.toastr.error('Please provide a longer response')
         return
      }

      if (product.opsPrice > product.currentPrice * 2.0) {
         this.pricingService.showValidationModal.set(true)
      }

      product.isSaving = true

      const apiCall = !passedScheduleDate
         ? this.pricingClient.overridePricingUpdateRequest(product)
         : isZoho
         ? this.pricingClient.createZohoTicketForChangeRequest({
              id: product.pricingChangeRequestId,
              opsPrice: product.opsPrice,
              finalComment: product.finalComment,
              actionedOn: new Date(),
           } as PricingChangeRequest)
         : this.pricingClient.overridePricingChangeRequest({
              id: product.pricingChangeRequestId,
              opsPrice: product.opsPrice,
              finalComment: product.finalComment,
              captainApproved: passedScheduleDate ? 1 : undefined,
              actionedOn: new Date(),
              notificationSent: null,
           } as PricingChangeRequest)

      ;(apiCall as Observable<any>).subscribe({
         next: (data) => {
            onSuccess(data)
            product.isSaving = false
         },
         error: () => {
            this.toastr.error('ERROR in communication with server. If the problem persists, please contact support!')
            product.isSaving = false
            onError()
         },
      })
   }

   saveReviewPrice(
      product: any,
      passedScheduleDate: boolean,
      isCaptain: boolean,
      showValidationModal: (msg: string) => void,
      onToggleEdit: () => void,
      onRecordUpdated: (id?: number) => void,
      onError?: () => void
   ): void {
      const price = product.newPrice

      if (price === 'NaN' || !this.isValidPrice(price)) {
         this.toastr.error('Only numeric values allowed')
         return
      }

      if (price <= 0) {
         this.toastr.error('Value must be greater than 0')
         return
      }

      if (!product.rationale?.trim()) {
         this.toastr.error('Rationale is required to continue')
         return
      }

      if (product.rationale.trim().length <= 5) {
         this.toastr.error('Please provide a longer response')
         return
      }

      // Show warning modal if extreme price difference
      if (price > product.currentPrice * 2.0 || price < product.currentPrice / 2.0) {
         showValidationModal(
            price > product.currentPrice
               ? 'The new price is more than double the current price.'
               : 'The new price is less than half the current price.'
         )
      }

      product.isSaving = true

      const request = passedScheduleDate
         ? this.pricingClient.addOrUpdatePricingChangeRequest({
              id: product.pricingChangeRequestId ?? 0,
              reviewPrice: price,
              reviewComment: product.rationale,
              opsPrice: product.opsPrice,
              finalComment: product.finalComment,
              practiceSKey: product.practiceSKey,
              recordType: product.recordType,
              pimsId: product.pimsId,
              liveSystemPrice: product.currentPrice,
           } as PricingChangeRequest)
         : this.pricingClient.savePricingUpdate(product)

      request.subscribe({
         next: (data) => {
            if (!isCaptain) {
               onToggleEdit()
            }

            if (passedScheduleDate) {
               product.pricingChangeRequestId = data
            }

            product.isSaving = false
            onRecordUpdated(data)
         },
         error: () => {
            product.isSaving = false
            this.toastr.error('ERROR in communication with server. If the problem persists, please contact support!')
            onError?.()
         },
      })
   }

   removeReviewPrice(product: any, passedScheduleDate: boolean, isCaptain: boolean, onUnhighlightRow: () => void, onRecordUpdated: () => void): void {
      const toSave = { ...product, newPrice: null, rationale: '' }

      const request = passedScheduleDate
         ? this.pricingClient.addOrUpdatePricingChangeRequest({
              id: product.pricingChangeRequestId,
              reviewPrice: null,
              reviewComment: '',
           } as PricingChangeRequest)
         : this.pricingClient.savePricingUpdate(toSave)

      product.isSaving = true

      request.subscribe({
         next: () => {
            product.newPrice = null
            product.rationale = ''
            product.pricingChangeRequestId = null

            if (!isCaptain) {
               onUnhighlightRow()
            }

            product.isSaving = false
            onRecordUpdated()
         },
         error: () => {
            product.isSaving = false
            this.toastr.error('ERROR in communication with server. If the problem persists, please contact support!')
         },
      })
   }

   approvePrice(product: any, passedScheduleDate: boolean, onRecordUpdated: () => void): void {
      const toSave = { ...product, opsPrice: product.newPrice }

      const request: Observable<any> = passedScheduleDate
         ? this.pricingClient.approvePricingChangeRequest({
              id: toSave.pricingChangeRequestId,
           } as PricingChangeRequest)
         : this.pricingClient.approvePricingUpdateRequest(toSave.id)

      product.isSaving = true

      request.subscribe({
         next: () => {
            product.opsPrice = product.newPrice
            product.captainApproved = 1
            product.isSaving = false
            onRecordUpdated()
         },
         error: () => {
            product.isSaving = false
            this.toastr.error('ERROR in communication with server. If the problem persist please contact support!')
         },
      })
   }

   removeOpsOverride(product: any, passedScheduleDate: boolean, onRecordUpdated: () => void, onError: () => void): void {
      const toSave = {
         ...product,
         opsPrice: null,
         finalComment: '',
         captainApproved: 0,
      }

      const apicall: Observable<any> = !passedScheduleDate
         ? this.pricingClient.overridePricingUpdateRequest(toSave)
         : this.pricingClient.overridePricingChangeRequest({
              id: toSave.pricingChangeRequestId,
              opsPrice: null,
              finalComment: '',
              captainApproved: 0,
              actionedOn: null,
              notificationSent: null,
           } as PricingChangeRequest)

      ;(apicall as Observable<any>).subscribe({
         next: () => {
            product.opsPrice = null
            product.finalComment = ''
            product.captainApproved = 0
            product.zohoTicketId = ''
            product.isSaving = false
            onRecordUpdated()
         },
         error: () => {
            product.isSaving = false
            onError()
         },
      })
   }
      */
}
